import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { formatMoney } from 'common'

import editPastReimbursement from 'core/application/adminDashboard/reimbursements/reimbursementsHistory/editPastReimbursement'
import { ReimbursementHistoryItem } from 'core/application/adminDashboard/reimbursements/reimbursementsHistory/getReimbursementsHistory'

import InputNumber from 'common/components/dataEntry/components/InputNumber'

import { Button, Modal } from 'common/components'
import { parseZoneFormat } from 'common/dateFormatters'
import { useCommandRequest } from 'common/hooks'

type EditPastReimbursmentModalModalProps = {
  isOpen: boolean
  onClose: () => void
  onSubmit: () => void
  selectedUser: ReimbursementHistoryItem
  planPeriodId: string
  hasESPR?: boolean
}

const EditPastReimbursmentModalModal = ({
  isOpen,
  onClose,
  onSubmit,
  selectedUser,
  planPeriodId,
  hasESPR = false,
}: EditPastReimbursmentModalModalProps) => {
  const { t: translate } = useTranslation()
  const [sendCommand, isLoading] = useCommandRequest()
  const [amount, setAmount] = useState(null)

  const handleAmountChange = (e: number) => {
    setAmount(e)
  }

  const handleSubmit = async () => {
    const result = await sendCommand(() =>
      editPastReimbursement({
        companyUserId: selectedUser.companyUserId,
        amount,
        scheduleDate: selectedUser.scheduleDate,
        planPeriodId,
      }),
    )

    if (result.completed) {
      onSubmit()
    }
  }

  const footer = () => (
    <div>
      <Button
        onClick={() => {
          onClose()
        }}
      >
        {translate('general.cancel')}
      </Button>
      <Button
        type="primary"
        htmlType="submit"
        className="ml-8"
        disabled={amount === null || amount > selectedUser.maxAmountOnEdit}
        loading={isLoading}
        onClick={handleSubmit}
      >
        {translate('general.submit')}
      </Button>
    </div>
  )

  return (
    <Modal onCancel={onClose} visible={isOpen} title={'Edit reimbursement amount'} footer={footer()} width={800}>
      <div className="color-gray-8">
        <div>{translate('admin-dashboard.reimbursements.reimbursements-history.edit-reimbursement-modal.header')}</div>
        <div className="flex flex__direction_column flex__align_center mt-16">
          <div className="flex mb-16">
            <div className="mr-8 text-semi-bold">
              {translate('admin-dashboard.reimbursements.reimbursements-history.edit-reimbursement-modal.employee')}:
            </div>
            <div>{selectedUser.fullName}</div>
          </div>
          <div className="flex mb-16">
            <div className="mr-8 text-semi-bold">
              {translate('admin-dashboard.reimbursements.reimbursements-history.edit-reimbursement-modal.approved-on')}:
            </div>
            <div>{parseZoneFormat(selectedUser.scheduleDate)}</div>
          </div>
          <div className="flex mb-16">
            <div className="mr-8 text-semi-bold">
              {translate(
                'admin-dashboard.reimbursements.reimbursements-history.edit-reimbursement-modal.original-amount',
              )}
              :
            </div>
            <div>{formatMoney(selectedUser.amount)}</div>
          </div>
          {hasESPR && (
            <>
              <div className="flex mb-16">
                <div className="mr-8 text-semi-bold">
                  {translate('admin-dashboard.reimbursements.reimbursements-history.taxed-label')}:
                </div>
                <div>{formatMoney(selectedUser.taxable)}</div>
              </div>
              <div className="flex mb-16">
                <div className="mr-8 text-semi-bold">
                  {translate('admin-dashboard.reimbursements.reimbursements-history.non-taxed-label')}:
                </div>
                <div>{formatMoney(selectedUser.nonTaxed)}</div>
              </div>
            </>
          )}

          <div className="flex flex__align_center mb-16">
            <div className="mr-8 text-semi-bold">
              {translate(
                'admin-dashboard.reimbursements.reimbursements-history.edit-reimbursement-modal.actual-amount',
              )}
              :
            </div>
            <div>
              <InputNumber
                size={'small'}
                type={'number'}
                addonBefore="$"
                onChange={handleAmountChange}
                max={selectedUser.maxAmountOnEdit}
              />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default EditPastReimbursmentModalModal
